/**
 * billing.ts — backend-agnostic billing display helpers.
 *
 * Plan source (Spec 7): the reactive Convex `myEntitlements` query, read
 * via `useEntitlements()`. The slug is derived from the entitled tiers
 * (hasSecurity → "security", else "base") — NOT the Clerk JWT `pla` claim,
 * which the Lemon Squeezy migration stopped sourcing from.
 *
 * This module now only maps a slug → display name/price (PLAN_META /
 * getPlanMeta). The "Manage subscription" URL moved to the Convex action
 * `api.billing.checkout.portalUrl` (LS hosted, signed + expiring), so the
 * old env-driven `getBillingPortalUrl` helper was removed.
 */

export type BillingPeriod = "monthly" | "yearly";

export interface PlanInfo {
  /** Human-readable name: "Base" | "Pro" | "Ultra Security" | "Enterprise" */
  name: string;
  /**
   * Display price string for the resolved cadence:
   * "$20/mo" | "$192/yr" | "$50/mo" | "$480/yr" | "Custom".
   */
  price: string;
  /** ISO slug: "base" | "security" | "enterprise" */
  slug: string;
  /**
   * Date the subscription was first activated (approximated as Clerk
   * `createdAt` in PR 2; Spec 5 will source from LS subscription object).
   * Null when the underlying date is unavailable or invalid.
   */
  activatedAt: Date | null;
}

// Per-cadence display prices, mirroring the live Lemon Squeezy variants and
// the PricingPage cards exactly (Base $20/mo · $192/yr · Pro $50/mo ·
// $480/yr · Ultra Security $100/mo · $960/yr). Enterprise is quote-based, so its price is
// cadence-independent ("Custom").
const PLAN_META: Record<string, { name: string; monthly: string; yearly: string }> = {
  base: { name: "Base", monthly: "$20/mo", yearly: "$192/yr" },
  pro: { name: "Pro", monthly: "$50/mo", yearly: "$480/yr" },
  security: { name: "Ultra Security", monthly: "$100/mo", yearly: "$960/yr" },
  enterprise: { name: "Enterprise", monthly: "Contact Us", yearly: "Contact Us" },
};

/**
 * Build PlanInfo from a slug + billing cadence + activation date. The slug is
 * derived upstream from `useEntitlements()`; the period from the reactive
 * `api.billing.queries.mySubscription` query — so an annual subscriber sees
 * their real yearly price instead of the monthly default (Codex). `period`
 * defaults to "monthly" for back-compat and for the no-subscription / loading
 * states where cadence is unknown.
 *
 * Returns null when the slug is unknown or missing so the UI can render
 * a "No active plan" state.
 */
export function getPlanMeta(
  slug: string | null | undefined,
  period: BillingPeriod = "monthly",
  createdAt: Date | null | undefined = null
): PlanInfo | null {
  if (typeof slug !== "string" || !slug) return null;
  const meta = PLAN_META[slug];
  if (!meta) return null;

  let activatedAt: Date | null = null;
  if (createdAt instanceof Date) {
    const ts = createdAt.getTime();
    activatedAt = Number.isNaN(ts) ? null : createdAt;
  }

  const price = period === "yearly" ? meta.yearly : meta.monthly;
  return { slug, name: meta.name, price, activatedAt };
}
